import pick from 'ramda/src/pick'
import mixpanel from 'root/src/client/logic/analytics/mixpanel'
import { mixpanelEvents } from 'root/src/shared/constants/mixpanel'
import updateEventDare from 'root/src/client/logic/dropEvent/actions/updateEventDare'
import { CREATE_PROJECT_DROP, GET_USER_DATA } from 'root/src/shared/descriptions/endpoints/endpointIds'
import apiRequest from 'root/src/client/logic/api/thunks/apiRequest'
import wait from 'root/src/shared/util/wait'
import { DROP_EVENT_PAGE_ROUTE_ID } from 'root/src/shared/descriptions/routes/routeIds'
import {
	GET_VERIFIED_MODAL_MODULE_ID,
	SUCCESS_MODAL_MODULE_ID,
} from 'root/src/shared/descriptions/modules/moduleIds'
import displayModal from 'root/src/client/logic/modal/thunks/displayModal'
import identity from 'ramda/src/identity'
import getDomainRoute from 'root/src/client/util/getDomainRoute'
import { events } from 'root/src/shared/constants/analytics'
import analyticsSendItem from 'root/src/client/logic/analytics/util/analyticsSendItem'
import userIdSelector from 'root/src/client/logic/auth/selectors/userIdSelector'
import userGroupsSelector from 'root/src/client/logic/auth/selectors/userGroupsSelector'
import { AuthGroup } from 'root/src/server/util/awsClients/authApiClient.types'
import { authModuleIds } from 'root/src/client/web/auth/descriptions/modules/authModuleIds'
import { displayHowToEarnPointsModal } from 'root/src/client/logic/dropEvent/thunks/displayHowToEarnPointsModal'
import { displayNoMoreRewardModal } from 'root/src/client/logic/dropEvent/thunks/displayNoMoreRewardModal'
import { displayUpdateContactInformationModal } from 'root/src/client/logic/project/helpers/displayUpdateContactInformationModal'
import accountDataSelector from 'root/src/client/logic/userData/selectors/accountDataSelector'
import { Platform } from 'root/src/shared/@types/platforms'
import { ourEmail } from 'root/src/shared/constants/mail'
import { getReadableProviderName } from 'root/src/client/v2/auth/helpers/providerHelpers'
import toast from 'react-hot-toast'

// eslint-disable-next-line prefer-template
export const generateMaxDaresText = (followerCount = 0, averageCcv = 0, averageYoutubeViews = 0, maxDares, platform) => {
	if (platform === Platform.Twitch) {
		return `Based on your 30 day concurrent views of ${averageCcv}, you can complete ${maxDares} dare${maxDares === 1 ? '' : 's'}.`
	}
	if (platform === Platform.YouTube) {
		return `Based on your 90 day average YouTube views of ${averageYoutubeViews}, you can complete ${maxDares} dare${maxDares === 1 ? '' : 's'}.`
	}
	if (platform === Platform.TikTok) {
		return `Based on your TikTok follower count of ${followerCount}, you can complete ${maxDares} dare${maxDares === 1 ? '' : 's'}.`
	}
	return `Based on your viewership, you can complete ${maxDares} dare${maxDares === 1 ? '' : 's'}.`
}

export default (formData, showModalFn = identity) => async (dispatch, getState) => {
	const state = getState()

	const userGroups = userGroupsSelector(state)
	const isCreator = userGroups.includes(AuthGroup.Creator)

	if (!isCreator) {
		dispatch(displayModal(authModuleIds.TRANSFORM_TO_CREATOR))
		throw { error: 'To be able to accept Dare project, you need to be a Creator' }
	}

	const accountData = accountDataSelector(state)
	if (accountData.isConfirmEmailPromptEnabled) {
		const isConfirmed = await displayUpdateContactInformationModal()
		if (!isConfirmed) {
			return {}
		}
		await dispatch(apiRequest(GET_USER_DATA))
	}

	const { dropEvent, suppressErrorModals } = formData
	const { id: campaignId, mnemonicId } = dropEvent
	const requestPayload = {
		...pick(['assignees', 'chosenInitialProductIndex', 'userHasGame', 'withoutReward'], formData),
		dropDareId: formData.dropDareId,
		campaignId,
	}
	const { error, body } = await dispatch(apiRequest(CREATE_PROJECT_DROP, requestPayload))

	if (error) {
		if (error.code === 'CampaignTermsNotAccepted') {
			// `suppressErrorModals` is set by the redesign accept flow: it renders its own redesign modal
			// from the thrown `code` instead. Legacy callers leave it falsy and keep the dispatch.
			if (!suppressErrorModals) {
				dispatch(displayHowToEarnPointsModal())
			}
			throw { error: 'You need to accept the campaign terms', code: error.code }
		}

		if (error.code === 'FollowerCountTooLow') {
			throw { error: 'Your follower count is too low' }
		}

		if (error.code === 'NotEnoughKeys') {
			if (!suppressErrorModals) {
				dispatch(displayNoMoreRewardModal({ formData }))
			}
			// Carry `formData` so the redesign "accept without reward" retry can re-submit the same payload.
			throw { error: 'Not enough keys', code: error.code, formData }
		}

		if (error.code === 'InsufficientBattlePassTier') {
			throw { error: error.message }
		}

		if (error.code === 'NotEnoughInitialProducts') {
			// Carry `code` so the redesign key selection modal can explain the waitlist instead of
			// surfacing a dead-end toast.
			throw { error: 'Out of keys, we\'ll email you when we get more', code: error.code }
		}

		if (error.code === 'DeliveryRatioTooLow') {
			const { campaignsData } = error.props
			if (!suppressErrorModals) {
				const campaignLinks = campaignsData.map(
					({ campaignName, campaignMnemonicId }) => ({
						linkText: campaignName,
						linkUrl: getDomainRoute(DROP_EVENT_PAGE_ROUTE_ID).replace(
							':recordId',
							campaignMnemonicId,
						),
					}),
				)
				dispatch(displayModal(SUCCESS_MODAL_MODULE_ID, {
					header: 'Delivery ratio is too low',
					errorText: 'You are not able to join this drop',
					...(campaignLinks.length > 0 ? {
						infoText: [
							`You have not delivered these Drop${campaignsData.length > 1 ? 's' : ''}:`,
							...campaignLinks,
							`If you need help, please contact us at ${ourEmail}`,
						],
					} : { infoText: '' }),
				}))
			}
			// Carry `campaignsData` so the redesign modal can render the same undelivered-drops list.
			throw { error: 'Delivery ratio is too low', code: error.code, campaignsData }
		}

		if (error.code === 'RequirementsNotSatisfied') {
			const { assigneesNotSatisfying } = error.props
			const [{ followerCount, averageCcv, maxDares, platform, averageYoutubeViews }] = assigneesNotSatisfying
			throw { error: generateMaxDaresText(followerCount, averageCcv, averageYoutubeViews, maxDares, platform) }
		}

		if (error.code === 'ProjectDuplicate' || error.code === 'SysError') {
			throw { error: error.message }
		}

		if (error.code === 'NotFound') {
			// dispatch(displayModal(NO_CAMPAIGN_MODAL_MODULE_ID))
			throw { error: 'Campaign is not active' }
		}

		if (error.code === 'VerificationPending') {
			if (!suppressErrorModals) {
				dispatch(displayModal(GET_VERIFIED_MODAL_MODULE_ID, { isCloseable: false }))
			}
			throw { error: error.message, code: error.code }
		}

		if (error.code === 'VerificationNotVerified') {
			dispatch(displayModal(SUCCESS_MODAL_MODULE_ID, {
				isCloseable: false,
				errorText: error.message,
				header: 'Your channel is not verified',
				infoText: null,
			}))
			throw { error: error.message }
		}

		if (error.code === 'VerificationRejected') {
			dispatch(displayModal(SUCCESS_MODAL_MODULE_ID, {
				isCloseable: false,
				errorText: error.message,
				header: 'Your channel is rejected',
				infoText: null,
			}))
			throw { error: error.message }
		}

		if (error.code === 'DareDisabled') {
			throw { error: 'Dare is no longer available' }
		}

		if (error.code === 'InternalServerError') {
			throw { error: 'Internal Server Error' }
		}

		if (error.code === 'ApiAuthError') {
			dispatch(displayModal(authModuleIds.SIGN_IN_WITH_PROVIDER_MODAL_MODULE, {
				provider: error.props.provider,
				header: 'Sign in to continue',
				popup: true,
				popupOptions: {
					onSuccess: () => {
						dispatch(displayModal(undefined))
						toast.success('Account successfully refreshed!', { duration: 3000 })
					},
					onError: (err) => {
						console.error(err)
						toast.error(err?.message || 'Something went wrong!')
					},
				},
				label: `Please re-authenticate your ${getReadableProviderName(error.props.provider)} account to deliver this Dare.`,
			}))

			throw { error: error.message }
		}

		console.error(error)

		throw { error: 'Something went wrong' }
	}
	mixpanel.track(mixpanelEvents.projectCreated)
	mixpanel.track(mixpanelEvents.dropClaim)
	const userId = userIdSelector(state)
	analyticsSendItem(events.acceptedDropDare, `${dropEvent.campaignName} - ${userId}`)
	// used to show INITIAL_PRODUCT_DISPLAY_MODAL after successfully project creation
	showModalFn()

	dispatch(updateEventDare({
		dropDareId: formData.dropDareId,
		mnemonicId,
		status: body?.status,
		expirationDate: body?.expirationDate,
		landingShortUrl: body?.landingShortUrl,
	}))
	if (formData.onSuccess) {
		await formData.onSuccess(body)
	}
	// this wait is here because projects aren't instantly created/pledged with elasticSearch
	await wait(1000)
	return { error,	body }
}
